import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useAppLanguage } from '../i18n'

// Fonction pour récupérer les formations WordPress (subjects)
const getSubjects = async () => {
  const response = await api.get('/subjects')
  const items: { id: number; title: string; source: string }[] = response.data || []
  return items.filter((s) => s.source === 'wordpress')
}

interface Props {
  open: boolean
  onClose: () => void
  onUploaded: () => void
  defaultCollectionId?: number | null
}

type Subject = { id: number; title: string }

export default function ContentUploadPanel({ open, onClose, onUploaded, defaultCollectionId }: Props) {
  const { t } = useAppLanguage()
  const [collections, setCollections] = useState<{ id: number; title: string }[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [collectionId, setCollectionId] = useState<number | null>(defaultCollectionId ?? null)
  const [subjectId, setSubjectId] = useState<number | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [uploadMode, setUploadMode] = useState<'file' | 'youtube'>('file')

  useEffect(() => {
    if (!open) return
    api.get('/subjects').then((r) => setCollections(r.data || [])).catch(() => setError(t.knowledges.upload.errorLoadSubjects))
    getSubjects().then(setSubjects).catch(() => setError(t.knowledges.upload.errorLoadSubjects))
  }, [open, t])

  const dropHandlers = useMemo(
    () => ({
      onDragOver: (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(true)
      },
      onDragLeave: () => setIsDragging(false),
      onDrop: (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)
        const dropped = Array.from(event.dataTransfer.files || [])
        if (dropped.length > 0) {
          setFiles(prev => [...prev, ...dropped])
          setError(null)
          setSuccess(null)
        }
      },
    }), [],
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      
      if (uploadMode === 'youtube') {
        // Upload YouTube transcript
        if (!youtubeUrl.trim()) {
          setError('Veuillez entrer une URL YouTube')
          return
        }

        setError(null)
        setSuccess(null)
        setIsUploading(true)

        try {
          const payload: any = { url: youtubeUrl }
          if (collectionId) payload.subject_id = collectionId
          else if (subjectId) payload.subject_id = subjectId

          const response = await api.post('/upload/youtube-transcript', payload)
          setSuccess(`Transcription YouTube importée avec succès ! (${response.data.snippet_count} segments, ${response.data.language})`)
          setYoutubeUrl('')
          onUploaded()
        } catch (e: any) {
          console.error('Erreur import YouTube:', e)
          setError(e.response?.data?.detail || 'Erreur lors de l\'import de la transcription YouTube')
        } finally {
          setIsUploading(false)
        }
      } else {
        // Upload files
        if (files.length === 0) {
          setError(t.knowledges.upload.errorSelectFile)
          return
        }

        setError(null)
        setSuccess(null)
        setProgress(0)
        setIsUploading(true)

        const results = await Promise.allSettled(files.map(async (file) => {
          const form = new FormData()
          form.append('file', file)
          form.append('title', file.name.replace(/\.[^/.]+$/, ''))
          if (collectionId) form.append('subject_id', String(collectionId))
          else if (subjectId) form.append('subject_id', String(subjectId))

          try {
            await api.post('/upload', form, {
              headers: { 'Content-Type': 'multipart/form-data' },
            })
          } catch (e: any) {
            console.error(`Erreur upload ${file.name}:`, e)
            throw e
          }
        }))

        const successCount = results.filter((r) => r.status === 'fulfilled').length
        const errorCount = results.filter((r) => r.status === 'rejected').length
        if (errorCount === 0) {
          setSuccess(t.knowledges.upload.success.replace('{n}', String(successCount)))
        } else {
          setSuccess(t.knowledges.upload.partialSuccess.replace('{success}', String(successCount)).replace('{error}', String(errorCount)))
        }
        setFiles([])
        setIsUploading(false)
        onUploaded()
      }
    },
    [collectionId, files, onUploaded, subjectId, uploadMode, youtubeUrl, t],
  )

  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <aside
        className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col overflow-hidden border-l border-emerald-100/70 bg-white/95 shadow-2xl shadow-emerald-500/30 transition-transform duration-300 ease-out dark:border-emerald-900/60 dark:bg-zinc-950/95"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between border-b border-emerald-100/70 px-6 py-5 dark:border-emerald-900/60">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
              {t.knowledges.upload.title}
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {t.knowledges.upload.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full px-3 py-1 text-sm font-medium text-zinc-600 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            {t.knowledges.upload.close}
          </button>
        </header>

        <div className="space-y-4 px-6 py-6">
          {/* Mode selector */}
          <div className="flex gap-2 border-b border-zinc-200 dark:border-zinc-700 pb-4">
            <button
              type="button"
              onClick={() => setUploadMode('file')}
              className={`flex-1 rounded-xl px-4 py-2 text-sm font-medium transition ${
                uploadMode === 'file'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
              }`}
            >
              📁 Fichiers
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('youtube')}
              className={`flex-1 rounded-xl px-4 py-2 text-sm font-medium transition ${
                uploadMode === 'youtube'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
              }`}
            >
              🎥 YouTube
            </button>
          </div>

          {/* Collection & Subject selects */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {t.knowledges.upload.collection}
              </label>
              <select
                value={collectionId ?? ''}
                onChange={(e) => setCollectionId(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              >
                <option value="">{t.knowledges.upload.choose}</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                {t.knowledges.upload.advice}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {t.knowledges.upload.course}
              </label>
              <select
                value={subjectId ?? ''}
                onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              >
                <option value="">{t.knowledges.upload.choose}</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                {t.knowledges.upload.courseHelp}
              </p>
            </div>
          </div>

          {/* Upload area - conditional based on mode */}
          {uploadMode === 'file' ? (
            <div
              onClick={() => document.getElementById('file-input')?.click()}
              onDrop={dropHandlers.onDrop}
              onDragOver={dropHandlers.onDragOver}
              onDragLeave={dropHandlers.onDragLeave}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition ${
                isDragging
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                  : 'border-zinc-300 bg-zinc-50 hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900/50'
              }`}
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept=".mp3,.wav,.mp4,.mov,.txt,.md,.pdf,.docx"
                onChange={(event) => {
                  const newFiles = Array.from(event.target.files || [])
                  setFiles(prev => [...prev, ...newFiles])
                  setError(null)
                  setSuccess(null)
                }}
                className="hidden"
              />
              <p className="text-sm text-zinc-700 dark:text-zinc-200">
                {t.knowledges.upload.dropzoneTitle}
              </p>
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                {t.knowledges.upload.formats}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                URL YouTube
              </label>
              <input
                type="text"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                💡 La transcription sera récupérée directement depuis YouTube (instantané, sans téléchargement)
              </p>
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 dark:bg-emerald-950/30 dark:border-emerald-900/50">
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  ✨ <strong>Formats supportés :</strong> youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/...
                </p>
              </div>
            </div>
          )}

          {uploadMode === 'file' && files.length > 0 && (
            <div className="max-h-40 overflow-y-auto space-y-2 text-left">
              {files.map((f, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white/50 dark:bg-zinc-800/50 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-100 truncate">{f.name}</p>
                    <p className="text-xs text-zinc-500">{(f.size / (1024 * 1024)).toFixed(2)} {t.knowledges.upload.mb}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFiles(prev => prev.filter((_, i) => i !== idx)) }}
                    className="ml-2 text-red-500 hover:text-red-700 text-lg font-bold"
                  >
                    ×
                  </button>
                </div>
              ))}
              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium text-center pt-2">
                {t.knowledges.upload.filesSelected.replace('{n}', String(files.length))}
              </p>
            </div>
          )}

          {/* Upload progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-xs text-zinc-500 dark:text-zinc-400">
                {progress < 100 ? `${t.knowledges.upload.uploading} (${progress}%)` : t.knowledges.upload.completed}
              </p>
            </div>
          )}

          {/* Error and success messages */}
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          )}

          {success && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-200">
              {success}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={() => {
                setFiles([])
                setYoutubeUrl('')
                setError(null)
                setSuccess(null)
              }}
              disabled={isUploading || (uploadMode === 'file' ? files.length === 0 : !youtubeUrl.trim())}
              className="rounded-xl px-4 py-2 text-sm font-medium text-zinc-600 transition hover:bg-zinc-100 disabled:opacity-50 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              {t.knowledges.upload.reset}
            </button>
            <button
              onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>)}
              disabled={isUploading || (uploadMode === 'file' ? files.length === 0 : !youtubeUrl.trim())}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-500 disabled:opacity-50"
            >
              {isUploading
                ? (uploadMode === 'youtube' ? 'Import en cours...' : t.knowledges.upload.uploading)
                : (uploadMode === 'youtube' ? 'Importer la transcription' : `${t.knowledges.upload.upload}${files.length > 1 ? ` (${files.length})` : ''}`)}
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-zinc-400 dark:text-zinc-500">
            {t.knowledges.upload.footer}
          </p>
        </div>
      </aside>
    </div>
  )
}
